import { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { API_URL, formatDate } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield,
  ShieldCheck,
  Users,
  UserPlus,
  UserCog,
  ChefHat,
  Coffee,
  Edit,
  Trash2,
  Mail,
  Lock,
  Search,
  Activity,
  Settings,
  Database,
  Server,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  Building,
  Key,
} from "lucide-react";

const AdminPanelPage = () => {
  const { t } = useLanguage();
  const { token, user: currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("all");

  // Modals
  const [createUserModal, setCreateUserModal] = useState(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Create User Form
  const [createForm, setCreateForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "staff",
  });

  // Edit Form
  const [editForm, setEditForm] = useState({
    role: "",
    is_active: true,
  });

  // System Stats
  const [systemStats, setSystemStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalOrders: 0,
    totalProducts: 0,
    totalTables: 0,
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [usersRes, statsRes, productsRes, tablesRes] = await Promise.all([
        axios.get(`${API_URL}/employees`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/dashboard/stats`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/products`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/tables`, { headers, withCredentials: true }),
      ]);
      
      setUsers(usersRes.data);
      setSystemStats({
        totalUsers: usersRes.data.length,
        activeUsers: usersRes.data.filter(u => u.is_active !== false).length,
        totalOrders: statsRes.data.order_count || 0,
        totalProducts: productsRes.data.length,
        totalTables: tablesRes.data.length,
      });
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/auth/register`, createForm, {
        headers,
        withCredentials: true,
      });
      toast.success("Utilizador criado com sucesso!");
      setCreateUserModal(false);
      setCreateForm({ name: "", email: "", password: "", role: "staff" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar utilizador");
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      role: user.role,
      is_active: user.is_active !== false,
    });
    setEditUserModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      // Update role
      await axios.put(
        `${API_URL}/employees/${selectedUser.user_id}/role?role=${editForm.role}`,
        {},
        { headers, withCredentials: true }
      );

      // Update status
      await axios.put(
        `${API_URL}/employees/${selectedUser.user_id}/status?is_active=${editForm.is_active}`,
        {},
        { headers, withCredentials: true }
      );

      toast.success("Utilizador atualizado!");
      setEditUserModal(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar utilizador");
    }
  };

  const handleDeleteUser = async () => {
    try {
      // Deactivate user instead of delete
      await axios.put(
        `${API_URL}/employees/${selectedUser.user_id}/status?is_active=false`,
        {},
        { headers, withCredentials: true }
      );
      toast.success("Utilizador desativado!");
      setDeleteConfirmModal(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao desativar utilizador");
    }
  };

  const roles = [
    { value: "admin", label: "Administrador", icon: ShieldCheck, color: "text-red-500", bg: "bg-red-500/10" },
    { value: "manager", label: "Gerente", icon: Shield, color: "text-purple-500", bg: "bg-purple-500/10" },
    { value: "staff", label: "Staff", icon: UserCog, color: "text-slate-500", bg: "bg-slate-500/10" },
    { value: "waiter", label: "Empregado de Mesa", icon: Coffee, color: "text-blue-500", bg: "bg-blue-500/10" },
    { value: "kitchen", label: "Cozinha", icon: ChefHat, color: "text-amber-500", bg: "bg-amber-500/10" },
  ];

  const getRoleInfo = (roleValue) => {
    return roles.find((r) => r.value === roleValue) || roles[2];
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === "all" || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-amber-500" />
            <h2 className="text-xl font-bold mb-2">Acesso Restrito</h2>
            <p className="text-muted-foreground">
              Esta página é apenas acessível a administradores.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-panel-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] flex items-center gap-3">
            <ShieldCheck className="w-8 h-8 text-primary" />
            Painel de Administração
          </h1>
          <p className="text-muted-foreground">Gestão completa do sistema e utilizadores</p>
        </div>
        <Button onClick={() => setCreateUserModal(true)} className="gap-2" data-testid="create-user-btn">
          <UserPlus className="w-4 h-4" />
          Novo Utilizador
        </Button>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="grid grid-cols-3 w-full max-w-lg">
          <TabsTrigger value="users" className="gap-2">
            <Users className="w-4 h-4" />
            Utilizadores
          </TabsTrigger>
          <TabsTrigger value="system" className="gap-2">
            <Server className="w-4 h-4" />
            Sistema
          </TabsTrigger>
          <TabsTrigger value="activity" className="gap-2">
            <Activity className="w-4 h-4" />
            Atividade
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {roles.map((role) => {
              const count = users.filter((u) => u.role === role.value).length;
              const Icon = role.icon;
              return (
                <Card key={role.value}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${role.bg}`}>
                      <Icon className={`w-5 h-5 ${role.color}`} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{count}</p>
                      <p className="text-xs text-muted-foreground">{role.label}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Pesquisar por nome ou email..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filtrar por função" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as Funções</SelectItem>
                {roles.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Users Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 font-medium">Utilizador</th>
                      <th className="text-left p-4 font-medium">Email</th>
                      <th className="text-left p-4 font-medium">Função</th>
                      <th className="text-left p-4 font-medium">Estado</th>
                      <th className="text-right p-4 font-medium">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => {
                      const roleInfo = getRoleInfo(user.role);
                      const isCurrentUser = user.user_id === currentUser?.user_id;
                      const Icon = roleInfo.icon;
                      
                      return (
                        <tr
                          key={user.user_id}
                          className={`border-b hover:bg-muted/30 ${isCurrentUser ? "bg-primary/5" : ""}`}
                          data-testid={`user-row-${user.user_id}`}
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={user.picture} />
                                <AvatarFallback className="bg-primary text-primary-foreground">
                                  {user.name?.charAt(0) || "U"}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium flex items-center gap-2">
                                  {user.name}
                                  {isCurrentUser && (
                                    <Badge variant="outline" className="text-xs">
                                      Você
                                    </Badge>
                                  )}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-muted-foreground">{user.email}</td>
                          <td className="p-4">
                            <Badge className={`${roleInfo.bg} ${roleInfo.color} border-0`}>
                              <Icon className="w-3 h-3 mr-1" />
                              {roleInfo.label}
                            </Badge>
                          </td>
                          <td className="p-4">
                            {user.is_active !== false ? (
                              <Badge className="bg-emerald-500/10 text-emerald-500 border-0">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Ativo
                              </Badge>
                            ) : (
                              <Badge className="bg-red-500/10 text-red-500 border-0">
                                <XCircle className="w-3 h-3 mr-1" />
                                Inativo
                              </Badge>
                            )}
                          </td>
                          <td className="p-4 text-right">
                            {!isCurrentUser && (
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditUser(user)}
                                  data-testid={`edit-user-${user.user_id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setDeleteConfirmModal(true);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-muted-foreground">
                          <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>Nenhum utilizador encontrado</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Tab */}
        <TabsContent value="system" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Utilizadores</p>
                    <p className="text-3xl font-bold">{systemStats.totalUsers}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-blue-500/10">
                    <Users className="w-6 h-6 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Produtos</p>
                    <p className="text-3xl font-bold">{systemStats.totalProducts}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-emerald-500/10">
                    <Database className="w-6 h-6 text-emerald-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Mesas</p>
                    <p className="text-3xl font-bold">{systemStats.totalTables}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-amber-500/10">
                    <Building className="w-6 h-6 text-amber-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pedidos Hoje</p>
                    <p className="text-3xl font-bold">{systemStats.totalOrders}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-purple-500/10">
                    <Activity className="w-6 h-6 text-purple-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Outfit'] flex items-center gap-2">
                  <Server className="w-5 h-5" />
                  Estado do Sistema
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <span>API Backend</span>
                  <Badge className="bg-emerald-500/10 text-emerald-500">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Online
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <span>Base de Dados</span>
                  <Badge className="bg-emerald-500/10 text-emerald-500">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Conectado
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <span>Autenticação Google</span>
                  <Badge className="bg-emerald-500/10 text-emerald-500">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Ativo
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <span>Serviços IA</span>
                  <Badge className="bg-amber-500/10 text-amber-500">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Verificar Chave
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-['Outfit'] flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Permissões por Função
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="p-3 rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <ShieldCheck className="w-4 h-4 text-red-500" />
                      <span className="font-semibold">Administrador</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Acesso total: gestão de utilizadores, configurações, relatórios, todos os módulos
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-purple-500" />
                      <span className="font-semibold">Gerente</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Gestão operacional: produtos, stock, funcionários, relatórios, caixa
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <Coffee className="w-4 h-4 text-blue-500" />
                      <span className="font-semibold">Empregado de Mesa</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      POS, mesas, pedidos, reservas
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <ChefHat className="w-4 h-4 text-amber-500" />
                      <span className="font-semibold">Cozinha</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Display de comandas, gestão de pedidos
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-['Outfit']">Atividade Recente</CardTitle>
              <CardDescription>Últimas ações no sistema</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {users.slice(0, 5).map((user, index) => (
                  <div key={index} className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Conta criada • {getRoleInfo(user.role).label}
                      </p>
                    </div>
                    <Badge variant="outline">Novo</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create User Modal */}
      <Dialog open={createUserModal} onOpenChange={setCreateUserModal}>
        <DialogContent data-testid="create-user-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Criar Novo Utilizador
            </DialogTitle>
            <DialogDescription>
              Crie uma conta para um novo membro da equipa
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome Completo</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="João Silva"
                required
                data-testid="create-user-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="joao@restaurante.pt"
                required
                data-testid="create-user-email"
              />
            </div>

            <div className="space-y-2">
              <Label>Palavra-passe</Label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="create-user-password"
              />
            </div>

            <div className="space-y-2">
              <Label>Função</Label>
              <Select
                value={createForm.role}
                onValueChange={(value) => setCreateForm({ ...createForm, role: value })}
              >
                <SelectTrigger data-testid="create-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[200]">
                  {roles.map((role) => {
                    const Icon = role.icon;
                    return (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${role.color}`} />
                          {role.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateUserModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="confirm-create-user">
                Criar Utilizador
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={editUserModal} onOpenChange={setEditUserModal}>
        <DialogContent data-testid="edit-user-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Editar Utilizador</DialogTitle>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="flex items-center gap-4 p-4 rounded-lg bg-muted">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={selectedUser.picture} />
                  <AvatarFallback>{selectedUser.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{selectedUser.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                </div>
              </div>

              {/* Role Selection */}
              <div className="space-y-2">
                <Label>Função</Label>
                <Select
                  value={editForm.role}
                  onValueChange={(value) => setEditForm({ ...editForm, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => {
                      const Icon = role.icon;
                      return (
                        <SelectItem key={role.value} value={role.value}>
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${role.color}`} />
                            {role.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Active Status */}
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div>
                  <Label>Conta Ativa</Label>
                  <p className="text-sm text-muted-foreground">
                    Desativar impede o acesso ao sistema
                  </p>
                </div>
                <Switch
                  checked={editForm.is_active}
                  onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditUserModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} data-testid="save-edit-user">
              Guardar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Modal */}
      <Dialog open={deleteConfirmModal} onOpenChange={setDeleteConfirmModal}>
        <DialogContent data-testid="delete-user-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-destructive flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Desativar Utilizador
            </DialogTitle>
            <DialogDescription>
              Tem a certeza que deseja desativar este utilizador? O utilizador não poderá aceder ao sistema.
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="flex items-center gap-4 p-4 rounded-lg bg-destructive/10 border border-destructive/30">
              <Avatar>
                <AvatarImage src={selectedUser.picture} />
                <AvatarFallback>{selectedUser.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold">{selectedUser.name}</p>
                <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteConfirmModal(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} data-testid="confirm-delete-user">
              Desativar Utilizador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanelPage;
